import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api, { getAll } from '../../services/api';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import { notify } from '../../utils/notifications';
import {
  Plus, Search, Eye, Edit, Trash2, Bus, Clock, School, X, CheckCircle, XCircle
} from 'lucide-react';

const Linee = () => {
  const { utente } = authStore();
  const modificabile = puoModificare(utente);
  const navigate = useNavigate();
  const [linee, setLinee] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLinea, setEditingLinea] = useState(null);
  const [formData, setFormData] = useState({
    codice: '',
    nome: '',
    descrizione: '',
    scuole_servite: '',
    orario_partenza: '',
    orario_arrivo: '',
    giorni_servizio: 'Lunedì-Venerdì',
    attiva: true,
    note: ''
  });

  useEffect(() => {
    caricaLinee();
  }, []);

  const caricaLinee = async () => {
    try {
      setLoading(true);
      setLinee(await getAll('/trasporti/linee/'));
    } catch (error) {
      console.error('Errore caricamento linee:', error);
      notify.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (linea = null) => {
    if (linea) {
      setEditingLinea(linea);
      setFormData(linea);
    } else {
      setEditingLinea(null);
      setFormData({
        codice: '',
        nome: '',
        descrizione: '',
        scuole_servite: '',
        orario_partenza: '',
        orario_arrivo: '',
        giorni_servizio: 'Lunedì-Venerdì',
        attiva: true,
        note: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLinea(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const toastId = notify.loading(editingLinea ? 'Aggiornamento...' : 'Creazione...');
    
    try {
      if (editingLinea) {
        await api.put(`/trasporti/linee/${editingLinea.id}/`, formData);
        notify.update(toastId, { type: 'success', render: 'Linea aggiornata!' });
      } else {
        await api.post('/trasporti/linee/', formData);
        notify.update(toastId, { type: 'success', render: 'Linea creata!' });
      }
      await caricaLinee();
      handleCloseModal();
    } catch (error) {
      console.error('Errore salvataggio linea:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Errore nel salvataggio';
      notify.update(toastId, { type: 'error', render: errorMsg });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo gruppo?')) return;
    
    const toastId = notify.loading('Eliminazione...');
    try {
      await api.delete(`/trasporti/linee/${id}/`);
      await caricaLinee();
      notify.update(toastId, { type: 'success', render: 'Linea eliminata!' });
    } catch (error) {
      console.error('Errore eliminazione linea:', error);
      notify.update(toastId, { type: 'error', render: 'Errore nell\'eliminazione' });
    }
  };

  const filteredLinee = linee.filter(linea =>
    linea.codice?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    linea.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    linea.scuole_servite?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1b94ce]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gruppi di linea</h1>
            <p className="text-sm text-gray-500 mt-0.5">Un gruppo per città servita: l'abbonamento vale su tutti i mezzi del gruppo</p>
          </div>
          {modificabile && (
          <button onClick={() => handleOpenModal()} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Nuovo Gruppo
          </button>
          )}
        </div>

        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per codice, nome o scuola..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-compact pl-9"
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          {filteredLinee.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                <Bus className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Nessun gruppo trovata</h3>
              <p className="text-sm text-gray-500 mb-4">
                {searchTerm ? 'Prova con termini diversi' : 'Inizia creando la prima linea'}
              </p>
              {!searchTerm && (
                <button onClick={() => handleOpenModal()} className="btn-primary inline-flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Crea Linea
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table-compact">
                <thead>
                  <tr>
                    <th>Gruppo</th>
                    <th className="text-center">Scuole</th>
                    <th>Orari</th>
                    <th>Servizio</th>
                    <th>Stato</th>
                    <th className="text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLinee.map((linea) => (
                    <tr key={linea.id}>
                      <td>
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <Bus className="w-4 h-4 text-[#1b94ce] flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{linea.codice}</div>
                            <div className="text-xs text-gray-500 truncate">{linea.nome}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <School className="w-4 h-4 text-indigo-600" />
                          <span className="text-sm font-semibold text-gray-900">
                            {(() => {
                              try {
                                // Prova a parsare scuole_servite come JSON
                                if (!linea.scuole_servite) return 0;
                                const scuoleStr = linea.scuole_servite.trim();
                                if (scuoleStr.startsWith('[') || scuoleStr.startsWith('{')) {
                                  const scuole = JSON.parse(scuoleStr);
                                  return Array.isArray(scuole) ? scuole.length : 0;
                                }
                                // Se è testo, conta le righe non vuote
                                return scuoleStr.split('\n').filter(s => s.trim()).length;
                              } catch {
                                return 0;
                              }
                            })()}
                          </span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap">
                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <Clock className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <div>{linea.orario_partenza || 'N/A'}</div>
                            <div className="text-gray-500">{linea.orario_arrivo || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap">
                        <span className="text-xs text-gray-600">{linea.giorni_servizio || 'N/A'}</span>
                      </td>
                      <td>
                        {linea.attiva ? (
                          <span className="badge bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 inline mr-1" />
                            Attiva
                          </span>
                        ) : (
                          <span className="badge bg-gray-100 text-gray-800">
                            <XCircle className="w-3 h-3 inline mr-1" />
                            Non Attiva
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => navigate(`/admin/linee/${linea.id}`)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition" title="Dettagli">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleOpenModal(linea)} className="p-1.5 text-green-600 hover:bg-green-50 rounded transition" title="Modifica">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(linea.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded transition" title="Elimina">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {filteredLinee.length > 0 && (
          <div className="mt-3 text-xs text-gray-500 text-right">
            {filteredLinee.length} {filteredLinee.length === 1 ? 'linea' : 'linee'}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">
                {editingLinea ? 'Modifica Gruppo' : 'Nuovo Gruppo'}
              </h2>
              <button onClick={handleCloseModal} className="p-1.5 hover:bg-gray-100 rounded transition">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Codice *</label>
                  <input type="text" required value={formData.codice} onChange={(e) => setFormData({ ...formData, codice: e.target.value })} className="input-compact" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Nome *</label>
                  <input type="text" required value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} className="input-compact" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Descrizione</label>
                <textarea value={formData.descrizione} onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })} rows={2} className="input-compact" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Scuole Servite</label>
                <input type="text" value={formData.scuole_servite} onChange={(e) => setFormData({ ...formData, scuole_servite: e.target.value })} className="input-compact" placeholder="Es: Scuola A, Scuola B" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Orario Partenza</label>
                  <input type="time" value={formData.orario_partenza} onChange={(e) => setFormData({ ...formData, orario_partenza: e.target.value })} className="input-compact" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Orario Arrivo</label>
                  <input type="time" value={formData.orario_arrivo} onChange={(e) => setFormData({ ...formData, orario_arrivo: e.target.value })} className="input-compact" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Giorni di Servizio</label>
                <input type="text" value={formData.giorni_servizio} onChange={(e) => setFormData({ ...formData, giorni_servizio: e.target.value })} className="input-compact" />
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="attiva" checked={formData.attiva} onChange={(e) => setFormData({ ...formData, attiva: e.target.checked })} className="w-4 h-4 text-[#1b94ce] border-gray-300 rounded focus:ring-[#1b94ce]" />
                <label htmlFor="attiva" className="text-sm text-gray-700">Linea attiva</label>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Note</label>
                <textarea value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} rows={2} className="input-compact" />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium">
                  Annulla
                </button>
                <button type="submit" className="btn-primary">
                  {editingLinea ? 'Salva' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Linee;
